import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alpha,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Pagination,
  Select,
  FormControl,
  useTheme,
  useMediaQuery,
  InputLabel,
  GlobalStyles
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { campaignApi, mediaTypeApi, referenceApi, api } from '../services/api';
import { 
  formatCurrency, 
  parseCurrency, 
  calculateUnused,
  generateMonthsRange,
  determineCampaignStatus
} from '../utils/calculations';
import CampaignDialog from '../components/CampaignDialog';
import GlowButton from '../components/GlowButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CampaignIcon from '@mui/icons-material/Campaign';
import ImportDialog from '../components/ImportDialog';
import usePermissions from '../hooks/usePermissions';
import SnapshotCalendarSelector from '../components/SnapshotCalendarSelector';

// Define consistent font sizes
const FONT_SIZES = {
  header: '0.9rem',
  subtitle: '0.75rem', 
  body: '0.75rem',
  caption: '0.7rem',
  small: '0.65rem',
  table: '0.7rem',
  tableHeader: '0.7rem',
  number: '0.7rem'
};

const Campaigns = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Permission checking
  const { hasPermission } = usePermissions();
  const canImport = hasPermission('import_data');
  const canCreateCampaigns = hasPermission('create_campaigns');
  const canEditCampaigns = hasPermission('edit_campaigns');
  const canEditAccruals = hasPermission('edit_accruals');
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentBrand, setCurrentBrand] = useState('All');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [brandsMap, setBrandsMap] = useState({});
  const [subBrandsMap, setSubBrandsMap] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [snapshotMonths, setSnapshotMonths] = useState([]);
  const [loadingMediaTypes, setLoadingMediaTypes] = useState({}); // Track loading state per campaign
  // State để lưu NTW totals cho từng campaign (dạng: { [campaignId]: { [mediaType]: ntwTotal } })
  const [ntwTotals, setNtwTotals] = useState({});
  const [ntwLoading, setNtwLoading] = useState({}); // { [campaignId]: true/false }

  // Available brands for filtering - reordered to prioritize Heineken
  const brands = ['All', 'Heineken', 'Tiger', 'Larue', 'Strongbow', 'EDW', 'Bivina', 'BiaViet'];

  // Function to get campaign type display name
  const getCampaignTypeDisplay = (campaignTypeValue) => {
    // If no value provided, return default
    if (!campaignTypeValue) {
      return 'Thematic';
    }
    
    // If it's already a string (name), return as is
    if (typeof campaignTypeValue === 'string' && isNaN(campaignTypeValue)) {
      return campaignTypeValue;
    }
    
    // If it's a number (ID), map to name
    const typeId = parseInt(campaignTypeValue);
    const foundType = campaignTypes.find(type => 
      type.campaign_type_id === typeId || type.id === typeId
    );
    return foundType ? (foundType.campaign_type_name || foundType.name) : 'Thematic';
  };

  // Fetch reference data (brands, sub-brands, campaign types)
  const fetchReferenceData = async () => {
    try {
      // Fetch brands
      const brandsResponse = await referenceApi.getBrands();
      if (brandsResponse.data && Array.isArray(brandsResponse.data)) {
        // Create a map of brand_id -> brand_name
        const brandsMapping = {};
        brandsResponse.data.forEach(brand => {
          brandsMapping[brand.brand_id] = brand.brand_name;
        });
        setBrandsMap(brandsMapping);
        
        // Fetch sub-brands for each brand
        const allSubBrands = {};
        for (const brand of brandsResponse.data) {
          const subBrandsResponse = await referenceApi.getSubBrands(brand.brand_id);
          if (subBrandsResponse.data && Array.isArray(subBrandsResponse.data)) {
            subBrandsResponse.data.forEach(subBrand => {
              allSubBrands[subBrand.sub_brand_id] = subBrand.sub_brand_name;
            });
          }
        }
        setSubBrandsMap(allSubBrands);
      }

      // Fetch campaign types
      try {
        const campaignTypesResponse = await referenceApi.getCampaignTypes();
        if (campaignTypesResponse.data && Array.isArray(campaignTypesResponse.data)) {
          setCampaignTypes(campaignTypesResponse.data);
        }
      } catch (typeErr) {
        console.error('Error fetching campaign types:', typeErr);
        // Set default campaign types if API fails
        setCampaignTypes([
          { campaign_type_id: 1, campaign_type_name: 'Thematic' },
          { campaign_type_id: 2, campaign_type_name: 'AWO' },
          { campaign_type_id: 3, campaign_type_name: 'Credential' },
          { campaign_type_id: 4, campaign_type_name: 'Festive/ Celebration' }
        ]);
      }
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

  // Fetch campaigns from API
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await campaignApi.getAllCampaigns();
      // Check response data and ensure it is an array
      console.log("Debug campaigns response:", response.data);
      if (response.data) {
        let newCampaigns = [];
        if (Array.isArray(response.data)) {
          newCampaigns = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          // Case where API returns {results: [...]} format
          newCampaigns = response.data.results;
        } else {
          // Case where API returns a non-array object
          console.error('API response is not an array:', response.data);
          setCampaigns([]);
          setError('Unexpected data format received from server');
          return;
        }
        
        // Preserve media types data that was already loaded
        setCampaigns(prevCampaigns => {
          return newCampaigns.map(newCampaign => {
            const existingCampaign = prevCampaigns.find(c => c.id === newCampaign.id);
            if (existingCampaign && existingCampaign.mediaTypes && existingCampaign.mediaTypesLoaded) {
              // Preserve loaded media types data
              return {
                ...newCampaign,
                mediaTypes: existingCampaign.mediaTypes,
                mediaTypesLoaded: existingCampaign.mediaTypesLoaded
              };
            }
            return newCampaign;
          });
        });
      } else {
        console.error('No data received from API');
        setCampaigns([]);
        setError('No data received from server');
      }
    } catch (err) {
      setError('Failed to load campaigns. Please try again.');
      console.error('Error fetching campaigns:', err);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch available snapshot months
  const fetchSnapshotMonths = useCallback(async () => {
    try {
      const response = await api.get('/system/snapshot/months/');
      if (response.data && response.data.months && Array.isArray(response.data.months)) {
        setSnapshotMonths(response.data.months);
      }
    } catch (err) {
      console.error('Error fetching snapshot months:', err);
      // If API fails, try to provide some sample months
      const currentDate = new Date();
      const months = [];
      for (let i = 0; i < 6; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        months.push(date.toISOString().substring(0, 7));
      }
      setSnapshotMonths(months);
    }
  }, []);

  // Load campaigns on component mount
  useEffect(() => {
    fetchReferenceData();
    fetchCampaigns();
    fetchSnapshotMonths();
  }, [fetchCampaigns, fetchSnapshotMonths]);

  // Filter campaigns based on search term and brand
  const filteredCampaigns = Array.isArray(campaigns) 
    ? campaigns.filter(campaign => {
        const campaignName = campaign.name || campaign.campaign_name || '';
        const brandName = (campaign.brand && brandsMap[campaign.brand]) || campaign.brand || '';
        const mediaTypeText = campaign.media_type || '';
        
        const matchesSearch = searchTerm === '' || 
          campaignName.toLowerCase().includes(searchTerm.toLowerCase()) || 
          brandName.toLowerCase().includes(searchTerm.toLowerCase()) || 
          mediaTypeText.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesBrand = currentBrand === 'All' || 
          (campaign.brand && brandsMap[campaign.brand] === currentBrand) || 
          campaign.brand === currentBrand;
        
        return matchesSearch && matchesBrand;
      })
    : [];

  // Calculate saving from DB total if available; always use backend saving regardless of campaign status
  const calculateSaving = (campaign) => {
    if (campaign.saving_total !== undefined && campaign.saving_total !== null) {
      return parseFloat(campaign.saving_total) || 0;
    }
    return 0;
  };

  // Add saving calculation to campaigns
  const campaignsWithSaving = filteredCampaigns.map(campaign => {
    const saving = calculateSaving(campaign);
    return {
      ...campaign,
      saving: saving
    };
  });

  // Prioritize Heineken brand in filtered results
  const sortedCampaigns = [...campaignsWithSaving].sort((a, b) => {
    const aBrandName = (a.brand && brandsMap[a.brand]) || a.brand || '';
    const bBrandName = (b.brand && brandsMap[b.brand]) || b.brand || '';
    
    if (aBrandName === 'Heineken' && bBrandName !== 'Heineken') return -1;
    if (aBrandName !== 'Heineken' && bBrandName === 'Heineken') return 1;
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedCampaigns.length / rowsPerPage);
  const displayedCampaigns = sortedCampaigns.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Toggle expand/collapse of a campaign row
  const toggleRowExpand = async (campaignId) => {
    const currentlyExpanded = expandedRows[campaignId];
    if (currentlyExpanded) {
      setExpandedRows((prev) => ({ ...prev, [campaignId]: false }));
      setLoadingMediaTypes(prev => ({ ...prev, [campaignId]: false }));
    } else {
      setExpandedRows((prev) => ({ ...prev, [campaignId]: true }));
      // Nếu chưa có ntwTotals cho campaign này thì fetch
      if (!ntwTotals[campaignId]) {
        fetchNtwTotals(campaignId);
      }
      // Media types logic giữ nguyên
      const existingCampaign = campaigns.find(c => c.id === campaignId);
      if (existingCampaign && existingCampaign.mediaTypes && existingCampaign.mediaTypes.length > 0 && existingCampaign.mediaTypesLoaded) {
        return;
      }
      await loadMediaTypes(campaignId);
    }
  };

  // Load media types for a campaign
  const loadMediaTypes = async (campaignId) => {
    // Set loading state
    setLoadingMediaTypes(prev => ({ ...prev, [campaignId]: true }));
    
    try {
      console.log(`Loading media types for campaign ${campaignId}...`);
      // Use the regular media types endpoint for current data
      const response = await api.get(`/campaigns-data/${campaignId}/media-types/`);
      const mediaTypesData = Array.isArray(response.data) ? response.data : [];
      
      console.log(`Received ${mediaTypesData.length} media types for campaign ${campaignId}:`, mediaTypesData);
      
      // Update the campaigns state with the media types
      setCampaigns(prevCampaigns => 
        prevCampaigns.map(campaign => {
          if (campaign.id === campaignId) {
            // Normalize media types data to use consistent field names
            const normalizedMediaTypes = mediaTypesData.map(mt => ({
              ...mt,
              // Use given_cost if available, otherwise use given
              given: mt.given !== undefined ? mt.given : mt.given_cost,
              // Add empty fields if they don't exist to prevent undefined errors
              id: mt.id || `${campaignId}_${mt.media_type}`,
              media_type: mt.media_type || 'Unknown',
              planning_gross_cost: mt.planning_gross_cost || 0,
              actual_gross_cost: mt.actual_gross_cost || 0,
              saving: mt.saving || 0, // Ensure saving field is included
            }));
            
            return {
              ...campaign,
              mediaTypes: normalizedMediaTypes,
              mediaTypesLoaded: true, // Flag to indicate data has been loaded
            };
          }
          return campaign;
        })
      );
      
      console.log(`Successfully loaded media types for campaign ${campaignId}`);
    } catch (err) {
      console.error('Error loading media types:', err);
      showSnackbar('Failed to load media types', 'error');
      
      // Clear the loading flag even on error
      setCampaigns(prevCampaigns => 
        prevCampaigns.map(campaign => {
          if (campaign.id === campaignId) {
            return {
              ...campaign,
              mediaTypes: [],
              mediaTypesLoaded: true, // Mark as loaded even if failed to prevent infinite loading
            };
          }
          return campaign;
        })
      );
    } finally {
      // Always clear loading state
      setLoadingMediaTypes(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  // Handle opening create campaign dialog
  const handleCreateCampaign = () => {
    setCurrentCampaign(null);
    setOpenDialog(true);
  };

  // Handle opening edit campaign dialog
  const handleEditCampaign = (campaign) => {
    setCurrentCampaign(campaign);
    setOpenDialog(true);
  };

  // Handle closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCampaign(null);
  };

  // Handle saving a campaign from dialog
  const handleSaveCampaign = async (campaignData) => {
    try {
      // Format the data if needed
      const campaign = {
        ...campaignData,
      };
      
      console.log('Saving campaign data from Campaigns.js:', campaign);
      
      // Create or update the campaign
      let response;
      let campaignId;
      
      if (currentCampaign) {
        // Editing existing campaign
        campaignId = currentCampaign.id;
        response = await campaignApi.updateCampaign(campaignId, campaign);
        showSnackbar('Campaign updated successfully', 'success');
      } else {
        // Creating new campaign
        response = await campaignApi.createCampaign(campaign);
        campaignId = response.data?.id || response.id;
        showSnackbar('Campaign created successfully', 'success');
      }
      
      console.log('Campaign saved, response:', response);
      
      // Clear media types cache for this campaign to force refresh
      if (campaignId) {
        console.log(`Clearing media types cache for campaign ${campaignId}`);
        setCampaigns(prevCampaigns => 
          prevCampaigns.map(c => {
            if (c.id === campaignId) {
              return {
                ...c,
                mediaTypes: [],
                mediaTypesLoaded: false
              };
            }
            return c;
          })
        );
        
        // Force reload media types if campaign is currently expanded
        if (expandedRows[campaignId]) {
          console.log(`Campaign ${campaignId} is expanded, force reloading media types...`);
          setTimeout(() => {
            loadMediaTypes(campaignId);
          }, 500); // Small delay to ensure campaigns list is refreshed first
        }
      }
      
      // Return the response so CampaignDialog can get the ID
      return response;
    } catch (err) {
      console.error('Error saving campaign:', err);
      showSnackbar('Failed to save campaign', 'error');
      throw err;
    } finally {
      // Refresh the campaigns list
      fetchCampaigns();
    }
  };

  // Navigate to Excel-like view for a specific media type
  const handleViewExcelView = (campaignId, mediaType) => {
    navigate(`/excel-view/${campaignId}/${mediaType}`);
  };

  // Show snackbar message
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle closing snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle exporting campaigns data
  const handleExport = () => {
    navigate('/upload-export');
  };
  
  // Handle import dialog
  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };
  
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
  };
  
  const handleImportSuccess = () => {
    // Refresh campaigns after successful import
    fetchCampaigns();
    showSnackbar('Data imported successfully', 'success');
    // Close dialog after a short delay
    setTimeout(() => {
      setImportDialogOpen(false);
    }, 1500);
  };

  // Handle snapshot month selection
  const handleSnapshotSelect = (snapshotMonth) => {
    if (snapshotMonth && snapshotMonth !== 'live') {
      // Navigate to snapshot view
      navigate(`/campaigns?snapshotMonth=${snapshotMonth}`);
    }
  };

  // Handle live data selection
  const handleLiveDataSelect = () => {
    navigate('/campaigns');
  };

  // Handle brand tab change
  const handleBrandChange = (event, newValue) => {
    setCurrentBrand(newValue);
    setPage(1); // Reset to first page when filter changes
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when search changes
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  // Get columns based on screen size
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['name', 'brand', 'status', 'actions'];
    } else if (isTablet) {
      return ['name', 'brand', 'type', 'status', 'budget', 'actions'];
    } else {
      return ['name', 'brand', 'subBrand', 'type', 'status', 'startDate', 'endDate', 'budget', 'planning', 'actual', 'saving', 'actions'];
    }
  };
  
  const visibleColumns = getVisibleColumns();

  // Hàm fetch và tính tổng NTW cho từng media type của campaign
  const fetchNtwTotals = async (campaignId) => {
    setNtwLoading(prev => ({ ...prev, [campaignId]: true }));
    try {
      let campaign = campaigns.find(c => c.id === campaignId);
      let mediaTypes = campaign?.mediaTypes;
      if (!mediaTypes || mediaTypes.length === 0) {
        await loadMediaTypes(campaignId);
        // Đợi state cập nhật
        await new Promise(resolve => setTimeout(resolve, 100));
        // Lấy lại campaign mới nhất từ state
        campaign = campaigns.find(c => c.id === campaignId);
        mediaTypes = campaign?.mediaTypes || [];
      }
      // Nếu vẫn chưa có, thử lấy lại sau 1 lần nữa
      if (!mediaTypes || mediaTypes.length === 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
        campaign = campaigns.find(c => c.id === campaignId);
        mediaTypes = campaign?.mediaTypes || [];
      }
      const ntwByMediaType = {};
      mediaTypes.forEach(mt => {
        if (!mt || !mt.media_type) return;
        let totalNtw = 0;
        if (Array.isArray(mt.monthly_data)) {
          mt.monthly_data.forEach(md => {
            totalNtw += parseFloat(md.ntw || 0);
          });
        }
        ntwByMediaType[mt.media_type] = totalNtw;
      });
      setNtwTotals(prev => ({ ...prev, [campaignId]: ntwByMediaType }));
    } catch (err) {
      setNtwTotals(prev => ({ ...prev, [campaignId]: {} }));
    } finally {
      setNtwLoading(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  // Render media type rows for a campaign
  const renderMediaTypeRows = (campaign) => {
    // Use mediaTypes field (from loadMediaTypes)
    let mediaTypes = campaign.mediaTypes || [];
    
    // Ensure mediaTypes is an array
    if (!Array.isArray(mediaTypes)) {
      mediaTypes = [];
    }
    
    // Check if data is still loading using dedicated loading state
    const isLoading = loadingMediaTypes[campaign.id] === true;
    
    // Debug logging
    console.log(`Rendering media types for campaign ${campaign.id}:`, {
      isLoading,
      mediaTypesCount: mediaTypes.length,
      mediaTypesLoaded: campaign.mediaTypesLoaded,
      expandedState: expandedRows[campaign.id],
      rawMediaTypes: mediaTypes
    });
    
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={12} align="center">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Loading media types...
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    
    // If we have data that was previously loaded but now missing, try to reload
    if (expandedRows[campaign.id] && mediaTypes.length === 0 && campaign.mediaTypesLoaded !== true) {
      console.log(`Media types missing for expanded campaign ${campaign.id}, attempting reload...`);
      // Don't auto-reload to prevent infinite loop, let user click again
      return (
        <TableRow>
          <TableCell colSpan={12} align="center">
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="textSecondary">
                Media types data lost. Please expand again.
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                Click the expand button again to reload media types.
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    
    // Deduplicate media types by media_type name and aggregate costs
    const uniqueMediaTypes = {};
    mediaTypes.forEach(mt => {
      if (!mt || !mt.media_type) return;
      
      const key = mt.media_type;
      
      const totalGiven = parseFloat(mt.given || mt.media_type_given || 0);
      const totalPlanning = parseFloat(mt.planning_gross_cost || 0);
      const totalActual = parseFloat(mt.actual_gross_cost || 0);
      const totalNtw = parseFloat(mt.ntw || 0);
      const totalSaving = parseFloat(mt.saving || 0);
      
      if (!uniqueMediaTypes[key]) {
        uniqueMediaTypes[key] = {
          id: mt.id,
          media_type: mt.media_type,
          given: totalGiven,
          planning_gross_cost: totalPlanning,
          actual_gross_cost: totalActual,
          ntw: totalNtw, // Giữ lại trường ntw
          saving: totalSaving
        };
      } else {
        // Aggregate costs for duplicate media types
        uniqueMediaTypes[key].given += totalGiven;
        uniqueMediaTypes[key].planning_gross_cost += totalPlanning;
        uniqueMediaTypes[key].actual_gross_cost += totalActual;
        uniqueMediaTypes[key].ntw += totalNtw; // Cộng dồn ntw nếu có nhiều dòng
        uniqueMediaTypes[key].saving += totalSaving; // Cộng dồn saving theo backend
      }
    });
    
    // Convert back to array
    mediaTypes = Object.values(uniqueMediaTypes);
    
    if (mediaTypes.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={12} align="center">
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="textSecondary">
                No media types available
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                This campaign may not have any media types configured yet.
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    // Determine which columns to show based on screen size
    const getMediaTypeColumns = () => {
      if (isMobile) {
        return ['mediaType', 'actions'];
      } else if (isTablet) {
        return ['mediaType', 'budget', 'actions'];
      } else {
        return ['mediaType', 'budget', 'planningCost', 'actualCost', 'ntwTotal', 'saving', 'actions'];
      }
    };

    const mediaTypeColumns = getMediaTypeColumns();
    const campaignBrandName = (campaign.brand && brandsMap[campaign.brand]) || campaign.brand || '';
    const campaignStatus = determineCampaignStatus(campaign);
    const isDone = campaignStatus === 'DONE';

    return (
      <React.Fragment>
        <TableRow>
          <TableCell colSpan={12} sx={{ 
            paddingBottom: 0, 
            paddingTop: 0,
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e0e0e0'
          }}>
            <Box sx={{ 
              p: { xs: 2, sm: 3 },
              m: { xs: 1, sm: 2 },
              borderRadius: 2,
              backgroundColor: 'white',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              position: 'relative'
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                mb: 3, 
                color: '#2e7d32',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                fontSize: { xs: '1rem', sm: '1.1rem' }
              }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  bgcolor: '#4caf50' 
                }} />
                Media Types for {campaign.name} ({campaignBrandName})
              </Typography>
              <Box sx={{ 
                overflowX: 'auto',
                borderRadius: 1.5,
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}>
                <Table size="small" sx={{ 
                  backgroundColor: 'white',
                  '& .MuiTableCell-root': {
                    fontSize: FONT_SIZES.table,
                    borderColor: 'rgba(0, 0, 0, 0.08)',
                    py: { xs: 1, sm: 1.2 },
                    px: { xs: 1, sm: 1.5 }
                  }
                }}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: '#f8f9fa',
                      '& .MuiTableCell-head': {
                        color: '#2e7d32',
                        fontWeight: 600,
                        fontSize: FONT_SIZES.tableHeader,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                        backgroundColor: '#f8f9fa'
                      }
                    }}>
                    {mediaTypeColumns.includes('mediaType') && <TableCell>Media Type</TableCell>}
                    {mediaTypeColumns.includes('budget') && <TableCell>Budget</TableCell>}
                    {mediaTypeColumns.includes('planningCost') && <TableCell>Planning Gross Cost</TableCell>}
                    {mediaTypeColumns.includes('actualCost') && <TableCell>Actual Gross Cost</TableCell>}
                    {mediaTypeColumns.includes('ntwTotal') && <TableCell>NTW total</TableCell>}
                    {mediaTypeColumns.includes('saving') && <TableCell>Saving</TableCell>}
                    {mediaTypeColumns.includes('actions') && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mediaTypes.map((mediaType) => {
                    // Use backend-provided media type saving if available
                    const mediaSaving = parseFloat(mediaType.saving || 0);
                    
                    // Lấy NTW total từ state
                    let ntwValue = '-';
                    if (ntwLoading[campaign.id]) {
                      ntwValue = <CircularProgress size={16} />;
                    } else if (ntwTotals[campaign.id] && ntwTotals[campaign.id][mediaType.media_type] !== undefined) {
                      ntwValue = formatCurrency(ntwTotals[campaign.id][mediaType.media_type]);
                    }
                    
                    return (
                    <TableRow key={`${campaign.id}-${mediaType.id || mediaType.media_type}`}>
                      {mediaTypeColumns.includes('mediaType') && (
                        <TableCell>
                          <Chip
                            label={mediaType.media_type}
                            sx={{ 
                              fontWeight: 500,
                              minWidth: 70,
                              textAlign: 'center',
                              fontSize: FONT_SIZES.table,
                              borderRadius: 1.5,
                              backgroundColor: mediaType.media_type === 'Digital' ? '#f0f7ff' :
                                              mediaType.media_type === 'OOH' ? '#f0f9f0' :
                                              mediaType.media_type === 'TV' ? '#fff8f0' :
                                              mediaType.media_type === 'Radio' ? '#f8f0ff' : '#f5f5f5',
                              color: mediaType.media_type === 'Digital' ? '#1976d2' :
                                     mediaType.media_type === 'OOH' ? '#2e7d32' :
                                     mediaType.media_type === 'TV' ? '#f57c00' :
                                     mediaType.media_type === 'Radio' ? '#7b1fa2' : '#616161',
                              border: `1px solid ${
                                mediaType.media_type === 'Digital' ? 'rgba(25, 118, 210, 0.2)' :
                                mediaType.media_type === 'OOH' ? 'rgba(46, 125, 50, 0.2)' :
                                mediaType.media_type === 'TV' ? 'rgba(245, 124, 0, 0.2)' :
                                mediaType.media_type === 'Radio' ? 'rgba(123, 31, 162, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                              }`,
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                            }}
                          />
                        </TableCell>
                      )}
                      {mediaTypeColumns.includes('budget') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.number,
                            fontWeight: 500,
                            color: '#2e7d32'
                          }}>
                            {mediaType.given ? formatCurrency(parseFloat(mediaType.given)) : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {mediaTypeColumns.includes('planningCost') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.number,
                            fontWeight: 500,
                            color: '#2e7d32'
                          }}>
                            {mediaType.planning_gross_cost ? formatCurrency(parseFloat(mediaType.planning_gross_cost)) : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {mediaTypeColumns.includes('actualCost') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.number,
                            fontWeight: 500,
                            color: '#2e7d32'
                          }}>
                            {mediaType.actual_gross_cost ? formatCurrency(parseFloat(mediaType.actual_gross_cost)) : '0 đ'}
                          </Typography>
                        </TableCell>
                      )}
                      {mediaTypeColumns.includes('ntwTotal') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.number,
                            fontWeight: 500,
                            color: '#2e7d32'
                          }}>
                            {mediaType.ntw !== undefined && mediaType.ntw !== null && !isNaN(Number(mediaType.ntw))
                              ? formatCurrency(Number(mediaType.ntw))
                              : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {mediaTypeColumns.includes('saving') && (
                        <TableCell align="right" sx={{ color: mediaSaving > 0 ? '#2e7d32' : mediaSaving < 0 ? '#d32f2f' : 'inherit' }}>
                          {formatCurrency(mediaSaving)}
                        </TableCell>
                      )}
                      {mediaTypeColumns.includes('actions') && (
                        <TableCell align="center">
                          <Tooltip title={`Edit ${mediaType.media_type} data`}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewExcelView(campaign.id, mediaType.media_type)}
                              sx={{ 
                                color: '#2e7d32',
                                bgcolor: 'rgba(46, 125, 50, 0.06)', 
                                border: '1px solid rgba(46, 125, 50, 0.2)',
                                borderRadius: 1.5,
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                '&:hover': { 
                                  bgcolor: 'rgba(46, 125, 50, 0.12)',
                                  borderColor: 'rgba(46, 125, 50, 0.3)',
                                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)'
                                } 
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  )})}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  // Handle delete campaign
  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      return;
    }

    try {
      await campaignApi.deleteCampaign(campaignId);
      // Remove the campaign from state
      setCampaigns(campaigns.filter(campaign => campaign.id !== campaignId));
      showSnackbar('Campaign deleted successfully');
    } catch (err) {
      console.error('Error deleting campaign:', err);
      showSnackbar('Failed to delete campaign', 'error');
    }
  };

  // Clean up duplicate media types for a campaign
  const handleCleanupDuplicateMediaTypes = async (campaignId) => {
    if (!campaignId) return;
    
    setCleaningDuplicates(true);
    try {
      const result = await mediaTypeApi.cleanupDuplicateMediaTypes(campaignId);
      
      if (result.success) {
        if (result.deleted.length > 0) {
          showSnackbar(`Removed ${result.deleted.length} duplicate media types`, 'success');
          
          // Reload media types if row is expanded
          if (expandedRows[campaignId]) {
            await loadMediaTypes(campaignId);
          }
        } else {
          showSnackbar('No duplicate media types found', 'info');
        }
      } else {
        showSnackbar(`Error: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error cleaning up duplicate media types:', error);
      showSnackbar('Error while removing duplicate media types', 'error');
    } finally {
      setCleaningDuplicates(false);
      handleActionMenuClose();
    }
  };

  // Actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleActionMenuOpen = (event, campaign) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };
  
  const handleActionMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };
  
  const handleActionEdit = () => {
    if (selectedCampaign) {
      handleEditCampaign(selectedCampaign);
    }
    handleActionMenuClose();
  };
  
  const handleActionDelete = () => {
    if (selectedCampaign) {
      if (window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
        handleDeleteCampaign(selectedCampaign.id);
      }
    }
    handleActionMenuClose();
  };
  
  const handleActionViewDetail = () => {
    if (selectedCampaign) {
      // Redirect to detail view if available
      navigate(`/campaigns/${selectedCampaign.id}`);
    }
    handleActionMenuClose();
  };
  
  const handleActionCleanupMediaTypes = () => {
    if (selectedCampaign) {
      if (window.confirm('Do you want to remove duplicate media types for this campaign?')) {
        handleCleanupDuplicateMediaTypes(selectedCampaign.id);
      }
    } else {
      handleActionMenuClose();
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          'html, body, #root': {
            overflowX: 'hidden !important',
            maxWidth: '100vw !important',
            margin: 0,
            padding: 0,
          },
          '*': {
            boxSizing: 'border-box !important',
          },
          '.MuiContainer-root': {
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
            maxWidth: '100% !important',
            width: '100% !important',
            margin: '0 auto !important',
          },
          '.MuiGrid-container': {
            width: '100% !important',
            margin: '0 !important',
          },
          '.MuiGrid-item': {
            maxWidth: '100% !important',
          },
          '.MuiPaper-root': {
            maxWidth: '100% !important',
          },
          '.MuiTableContainer-root': {
            maxWidth: '100% !important',
            overflowX: 'auto !important',
          },
        }}
      />
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fffe',
        py: { xs: 2, sm: 3 },
        px: 0,
        overflow: 'hidden',
        overflowY: 'auto',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box'
      }}>
        <Container maxWidth="xl" sx={{ 
          px: { xs: 1.5, sm: 2, md: 3 },
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Section */}
          <Paper elevation={0} sx={{ 
            mb: { xs: 1.5, sm: 2, md: 2.5 },
            p: { xs: 2, sm: 2.5, md: 3 },
                         borderRadius: 2,
             background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
             color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 0
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <CampaignIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 0.5,
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    Campaign Management
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    opacity: 0.9,
                    fontWeight: 400
                  }}>
                    Manage your marketing campaigns, budgets, and performance tracking
                  </Typography>
                </Box>
              </Box>
              
              {/* Quick Stats */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                mt: 2,
                flexWrap: 'wrap'
              }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  p: 1.5,
                  minWidth: 120,
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {sortedCampaigns.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Total Campaigns
                  </Typography>
                </Box>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  p: 1.5,
                  minWidth: 120,
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {sortedCampaigns.filter(c => determineCampaignStatus(c) === 'DONE').length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Completed
                  </Typography>
                </Box>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  p: 1.5,
                  minWidth: 120,
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {new Set(sortedCampaigns.map(c => ((c.brand && brandsMap[c.brand]) || c.brand || '')).filter(Boolean)).size}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Active Brands
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Brand tabs */}
          <Paper sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid rgba(76, 175, 80, 0.2)',
            backgroundColor: 'background.paper',
            mb: { xs: 1, sm: 1.5 },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
          }}>
            <Box sx={{ 
                             background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
               borderBottom: '2px solid',
               borderColor: '#4caf50',
              px: 0,
              py: 0,
              width: '100%',
              overflow: 'hidden'
            }}>
              <Tabs 
                value={currentBrand}
                onChange={handleBrandChange}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  width: '100%',
                  minHeight: { xs: 32, sm: 36 },
                  '& .MuiTab-root': {
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: FONT_SIZES.body,
                    minHeight: { xs: 40, sm: 44 },
                    textTransform: 'none',
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 0.5, sm: 0.75 },
                    minWidth: { xs: 'auto', sm: 70 },
                    maxWidth: { xs: 110, sm: 'none' },
                    borderRadius: '8px 8px 0 0',
                    margin: '0 2px',
                    transition: 'all 0.3s ease',
                    '&.Mui-selected': {
                      color: '#2e7d32',
                      fontWeight: 700,
                      backgroundColor: 'rgba(76, 175, 80, 0.12)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                    },
                    '&:hover': {
                      color: '#2e7d32',
                      backgroundColor: 'rgba(76, 175, 80, 0.06)',
                      transform: 'translateY(-1px)',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#4caf50',
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                  },
                  '& .MuiTabs-scrollButtons': {
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  },
                }}
              >
                {brands.map((brand) => (
                  <Tab 
                    key={brand} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {brand === 'Heineken' && (
                          <StarIcon 
                            fontSize="small" 
                            sx={{ 
                              color: theme.palette.secondary.main, 
                              fontSize: { xs: 14, sm: 16 }
                            }} 
                          />
                        )}
                        <Typography 
                          component="span" 
                          sx={{ 
                            fontSize: 'inherit',
                            fontWeight: 'inherit'
                          }}
                        >
                          {isMobile && brand.length > 8 ? brand.substring(0, 6) + '...' : brand}
                        </Typography>
                      </Box>
                    }
                    value={brand} 
                  />
                ))}
              </Tabs>
            </Box>
          </Paper>

          {/* Search and action buttons */}
          <Paper sx={{ 
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            mb: { xs: 2, sm: 2.5 },
            backgroundColor: 'white',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  placeholder="Search campaigns by name, brand, type..."
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  fullWidth
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: FONT_SIZES.body,
                      height: '44px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.2)',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: FONT_SIZES.body,
                      fontWeight: 400
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ 
                          fontSize: 20,
                          color: '#666'
                        }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SnapshotCalendarSelector
                  availableSnapshots={snapshotMonths}
                  selectedSnapshot={null}
                  onSnapshotSelect={handleSnapshotSelect}
                  onLiveDataSelect={handleLiveDataSelect}
                  sx={{
                    '& .MuiPaper-root': {
                      height: '44px',
                      fontSize: FONT_SIZES.body,
                      backgroundColor: '#f8f9fa',
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                      },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack 
                  direction="row" 
                  spacing={1}
                  justifyContent={{ xs: 'center', md: 'flex-end' }}
                  sx={{ width: '100%' }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                    onClick={fetchCampaigns}
                    sx={{ 
                      fontSize: FONT_SIZES.body,
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      height: '44px',
                      borderRadius: 2,
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      color: '#666',
                      backgroundColor: 'white',
                      textTransform: 'none',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                        color: '#2e7d32',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  {canImport && (
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                      onClick={handleOpenImportDialog}
                      sx={{ 
                        fontSize: FONT_SIZES.body,
                        fontWeight: 500,
                        px: 2.5,
                        py: 1,
                        height: '44px',
                        borderRadius: 2,
                        backgroundColor: '#d32f2f',
                        color: 'white',
                        textTransform: 'none',
                        boxShadow: '0 2px 8px rgba(211, 47, 47, 0.25)',
                        '&:hover': {
                          backgroundColor: '#c62828',
                          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                        }
                      }}
                    >
                      Import
                    </Button>
                  )}
                  {canCreateCampaigns && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                      onClick={handleCreateCampaign}
                      sx={{ 
                        fontSize: FONT_SIZES.body,
                        fontWeight: 500,
                        px: 2.5,
                        py: 1,
                        height: '44px',
                        borderRadius: 2,
                        backgroundColor: '#2e7d32',
                        color: 'white',
                        textTransform: 'none',
                        boxShadow: '0 2px 8px rgba(46, 125, 50, 0.25)',
                        '&:hover': {
                          backgroundColor: '#1b5e20',
                          boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                        }
                      }}
                    >
                      New
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper elevation={3} sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px',
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          my: 4
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading campaigns...
            </Typography>
          </Box>
        </Paper>
      ) : displayedCampaigns.length === 0 ? (
        <Paper elevation={2} sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
        }}>
          <Box sx={{ mb: 2 }}>
            <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
          </Box>
          <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
            No campaigns found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please create your first campaign to get started.
          </Typography>
        </Paper>
      ) : (
          <Paper elevation={4} sx={{ 
            width: '100%', 
            overflow: 'hidden', 
            overflowX: 'hidden',
            borderRadius: 2, 
            mb: { xs: 1, sm: 1.5 },
            border: 'none',
            backgroundColor: 'background.paper',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <TableContainer sx={{ 
              width: '100%', 
              overflowX: 'auto',
              maxWidth: '100%',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'rgba(0,0,0,0.3)',
              },
            }}>
                                              <Table sx={{ 
                  minWidth: { xs: 400, sm: 480, md: 560 },
                  width: '100%',
                  '& .MuiTableCell-root': {
                    fontSize: FONT_SIZES.table,
                    py: { xs: 0.3, sm: 0.4 },
                    px: { xs: 0.3, sm: 0.4 },
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}>
                <TableHead>
                  <TableRow sx={{ 
                                         background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
                    '& .MuiTableCell-head': {
                      fontWeight: 700,
                      fontSize: FONT_SIZES.tableHeader,
                      color: 'black', // Đổi từ 'white' sang 'black'
                      borderBottom: 'none',
                      py: { xs: 0.75, sm: 1 },
                      px: { xs: 0.5, sm: 0.75 },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textShadow: 'none' // Bỏ hiệu ứng bóng chữ
                    }
                  }}>
                    <TableCell padding="checkbox" width="24px">
                      <Typography variant="caption" fontWeight="700" sx={{
                        fontSize: FONT_SIZES.tableHeader,
                        color: 'white'
                      }}>
                        ⚡
                      </Typography>
                    </TableCell>
                    {visibleColumns.includes('name') && (
                      <TableCell sx={{ minWidth: { xs: 80, sm: 95 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Campaign Name
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('brand') && (
                      <TableCell sx={{ minWidth: { xs: 45, sm: 60 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Brand
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('subBrand') && (
                      <TableCell sx={{ minWidth: { xs: 45, sm: 60 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Sub-Brand
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('type') && (
                      <TableCell sx={{ minWidth: { xs: 40, sm: 50 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Type
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('status') && (
                      <TableCell sx={{ minWidth: { xs: 45, sm: 60 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Status
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('startDate') && (
                      <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Start Date
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('endDate') && (
                      <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          End Date
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('budget') && (
                      <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Budget
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('planning') && (
                      <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Planning Cost
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('actual') && (
                      <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Actual Cost
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('saving') && (
                      <TableCell sx={{ minWidth: { xs: 60, sm: 95 } }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Saving
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.includes('actions') && (
                      <TableCell align="center" width="40px">
                        <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                          Actions
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
              <TableBody>
                {displayedCampaigns.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    <TableRow 
                      hover
                      sx={{ 
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => toggleRowExpand(campaign.id)}
                          sx={{ 
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {expandedRows[campaign.id] ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      {visibleColumns.includes('name') && (
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} sx={{ 
                            fontSize: FONT_SIZES.table,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: { xs: '120px', sm: '150px' }
                          }}>
                            {campaign.name || campaign.campaign_name || `Campaign ${campaign.id}`}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('brand') && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ 
                              fontSize: FONT_SIZES.table,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {/* Use brand mapping for current data */}
                              {campaign.brand && brandsMap[campaign.brand] ? brandsMap[campaign.brand] : campaign.brand}
                            </Typography>
                            {(campaign.brand === 'Heineken' || brandsMap[campaign.brand] === 'Heineken') && (
                              <Box 
                                component="span" 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  ml: 0.5,
                                  color: theme.palette.secondary.main,
                                }}
                              >
                                <StarIcon sx={{ fontSize: 14 }} />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      {visibleColumns.includes('subBrand') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.table,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {/* Use sub-brand mapping for current data */}
                            {campaign.sub_brand && subBrandsMap[campaign.sub_brand] 
                              ? subBrandsMap[campaign.sub_brand] 
                              : (campaign.sub_brand || '-')}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('type') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.table,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {getCampaignTypeDisplay(campaign.campaign_type || campaign.type)}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('status') && (
                        <TableCell>
                          <Chip 
                            label={determineCampaignStatus(campaign)} 
                            size="small"
                            sx={{
                              backgroundColor: 
                                determineCampaignStatus(campaign) === 'DONE'
                                  ? '#9e9e9e' // Grey for DONE
                                  : determineCampaignStatus(campaign) === 'ON-GOING'
                                  ? '#4caf50' // Green for ON-GOING
                                  : '#ffc107', // Yellow for PLANNING
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: FONT_SIZES.table,
                              height: '22px'
                            }} 
                          />
                        </TableCell>
                      )}
                      {visibleColumns.includes('startDate') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.table,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {campaign.start_date 
                              ? format(new Date(campaign.start_date), 'dd MMMM y') 
                              : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('endDate') && (
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            fontSize: FONT_SIZES.table,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {campaign.end_date 
                              ? format(new Date(campaign.end_date), 'dd MMMM y')
                              : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('budget') && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontWeight: 500, 
                              fontSize: FONT_SIZES.number,
                              color: campaign.brand === 'Heineken' ? theme.palette.primary.dark : 'inherit',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {campaign.given_lc ? formatCurrency(parseFloat(campaign.given_lc)) :
                             campaign.given_cost ? formatCurrency(parseFloat(campaign.given_cost)) :
                             campaign.total_given ? formatCurrency(parseFloat(campaign.total_given)) :
                             campaign.total_planning_cost ? formatCurrency(parseFloat(campaign.total_planning_cost)) : '0 đ'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('planning') && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontWeight: 500, 
                              fontSize: FONT_SIZES.number,
                              color: campaign.brand === 'Heineken' ? theme.palette.primary.dark : 'inherit',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {campaign.planning_gross_cost ? formatCurrency(campaign.planning_gross_cost) :
                             campaign.planning_gross_cost_lc ? formatCurrency(campaign.planning_gross_cost_lc) :
                             campaign.planning_total ? formatCurrency(campaign.planning_total) :
                             campaign.planning_cost ? formatCurrency(campaign.planning_cost) : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('actual') && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontWeight: 500, 
                              fontSize: FONT_SIZES.number,
                              color: campaign.brand === 'Heineken' ? theme.palette.primary.dark : 'inherit',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {campaign.actual_gross_cost ? formatCurrency(campaign.actual_gross_cost) :
                             campaign.actual_gross_cost_lc ? formatCurrency(campaign.actual_gross_cost_lc) :
                             campaign.actual_total ? formatCurrency(campaign.actual_total) :
                             campaign.actual_cost ? formatCurrency(campaign.actual_cost) : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.includes('saving') && (
                        <TableCell align="right" sx={{ color: campaign.saving > 0 ? '#2e7d32' : campaign.saving < 0 ? '#d32f2f' : 'inherit' }}>
                          {formatCurrency(calculateSaving(campaign))}
                        </TableCell>
                      )}
                      {visibleColumns.includes('actions') && (
                        <TableCell align="center">
                          <IconButton 
                            size="small"
                            onClick={(event) => handleActionMenuOpen(event, campaign)}
                            sx={{ 
                              color: theme.palette.text.secondary,
                              '&:hover': { 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main
                              } 
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                    {expandedRows[campaign.id] && renderMediaTypeRows(campaign)}
                  </React.Fragment>
                ))}
                
                {/* Total Row */}
                <TableRow sx={{ 
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  '& .MuiTableCell-root': {
                    fontWeight: 'bold',
                    borderTop: '2px solid #1976d2',
                    padding: '8px 12px',
                    fontSize: '0.7rem',
                    color: 'black'
                  }
                }}>
                  <TableCell padding="checkbox">
                    <Typography variant="body2" fontWeight="bold" sx={{ 
                      fontSize: '0.7rem',
                      color: 'black'
                    }}>
                      Σ
                    </Typography>
                  </TableCell>
                  {visibleColumns.includes('name') && (
                    <TableCell sx={{ 
                      minWidth: 160,
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      color: 'black'
                    }}>
                      TOTAL ({displayedCampaigns.length} campaigns)
                    </TableCell>
                  )}
                  {visibleColumns.includes('brand') && (
                    <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                      All Brands
                    </TableCell>
                  )}
                  {visibleColumns.includes('subBrand') && (
                    <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                      All Sub-brands
                    </TableCell>
                  )}
                  {visibleColumns.includes('type') && (
                    <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                      All Types
                    </TableCell>
                  )}
                  {visibleColumns.includes('status') && (
                    <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                      All Status
                    </TableCell>
                  )}
                  {visibleColumns.includes('startDate') && (
                    <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                      -
                    </TableCell>
                  )}
                  {visibleColumns.includes('endDate') && (
                    <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                      -
                    </TableCell>
                  )}
                  {visibleColumns.includes('budget') && (
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.65rem',
                      minWidth: 100,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'black'
                    }}>
                      {formatCurrency(
                        displayedCampaigns.reduce((sum, campaign) => {
                          const budgetValue = parseFloat(
                            campaign.given_lc || 
                            campaign.given_cost || 
                            campaign.total_given || 
                            campaign.total_planning_cost || 
                            0
                          );
                          return sum + budgetValue;
                        }, 0)
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('planning') && (
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.65rem',
                      minWidth: 100,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'black'
                    }}>
                      {formatCurrency(
                        displayedCampaigns.reduce((sum, campaign) => {
                          const planningValue = parseFloat(
                            campaign.planning_gross_cost || 
                            campaign.planning_gross_cost_lc || 
                            campaign.planning_total || 
                            campaign.planning_cost || 
                            0
                          );
                          return sum + planningValue;
                        }, 0)
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('actual') && (
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.65rem',
                      minWidth: 100,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'black'
                    }}>
                      {formatCurrency(
                        displayedCampaigns.reduce((sum, campaign) => {
                          const actualValue = parseFloat(
                            campaign.actual_gross_cost || 
                            campaign.actual_gross_cost_lc || 
                            campaign.actual_total || 
                            campaign.actual_cost || 
                            0
                          );
                          return sum + actualValue;
                        }, 0)
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('saving') && (
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.65rem',
                      minWidth: 100,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: (() => {
                        const totalSaving = displayedCampaigns.reduce((sum, campaign) => {
                          return sum + calculateSaving(campaign);
                        }, 0);
                        return totalSaving > 0 ? '#2e7d32' : totalSaving < 0 ? '#d32f2f' : 'black';
                      })()
                    }}>
                      {(() => { const totalSaving = displayedCampaigns.reduce((sum, c) => sum + calculateSaving(c), 0); return formatCurrency(totalSaving); })()}
                    </TableCell>
                  )}
                  {visibleColumns.includes('actions') && (
                    <TableCell align="center" sx={{ fontSize: '0.65rem', color: 'black' }}>
                      -
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
            {/* Pagination */}
            <Divider sx={{ borderColor: 'grey.200' }} />
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              p: { xs: 0.5, sm: 1 },
              gap: { xs: 0.5, sm: 0.25 },
              backgroundColor: 'grey.50',
              borderRadius: '0 0 6px 6px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{
                  fontSize: FONT_SIZES.caption,
                  fontWeight: 500
                }}>
                  Rows per page:
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  size="small"
                  sx={{ 
                    height: { xs: 22, sm: 26 },
                    minWidth: { xs: 40, sm: 50 },
                    borderRadius: 0.5,
                    backgroundColor: 'background.paper',
                    fontSize: FONT_SIZES.caption,
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <MenuItem value={10} sx={{ fontSize: 'inherit' }}>10</MenuItem>
                  <MenuItem value={25} sx={{ fontSize: 'inherit' }}>25</MenuItem>
                  <MenuItem value={50} sx={{ fontSize: 'inherit' }}>50</MenuItem>
                </Select>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                width: { xs: '100%', sm: 'auto' },
                gap: { xs: 0.25, sm: 0.5 }
              }}>
                <Typography variant="caption" color="text.secondary" sx={{
                  fontSize: FONT_SIZES.caption,
                  fontWeight: 500,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' }
                }}>
                  {`${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, sortedCampaigns.length)} of ${sortedCampaigns.length}`}
                </Typography>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  shape="rounded" 
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: FONT_SIZES.caption,
                      minWidth: { xs: 20, sm: 24 },
                      height: { xs: 20, sm: 24 },
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Box>
        </Paper>
      )}

      {/* Campaign Dialog */}
      <CampaignDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveCampaign}
        initialData={currentCampaign}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{
            ...(snackbar.severity === 'success' && {
              bgcolor: theme.palette.primary.main
            })
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Import Dialog */}
      <ImportDialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        onSuccess={handleImportSuccess}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionMenuClose}
      >
        {canEditCampaigns && (
          <MenuItem onClick={handleActionEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Campaign
          </MenuItem>
        )}
        <MenuItem onClick={handleActionViewDetail}>
          <MoreVertIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem 
          onClick={handleActionCleanupMediaTypes}
          disabled={cleaningDuplicates}
        >
          {cleaningDuplicates ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Cleaning...
            </>
          ) : (
            <>
              <FilterListIcon fontSize="small" sx={{ mr: 1 }} />
              Clean Up Media Types
            </>
          )}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Campaign
        </MenuItem>
      </Menu>
        </Container>
      </Box>
    </>
  );
};

export default Campaigns; 